import { useMutation, useQueryClient } from 'react-query';

import type { IStandardErrorResponse } from 'src/api/common';
import { useNotifications } from 'src/notifications';
import { apiClient } from 'src/services/api/client';
import { queryKeys } from 'src/services/api/constants';
import { GET_LEADS_PATH } from 'src/services/api/leads/constants';
import { getApiErrorMessage } from 'src/services/api/utils';
import {
    fromWire,
    getLeadFactsQueryKey,
    type ILeadFactSuggestion,
    type LeadFactSuggestionWire,
} from 'src/services/api/leads/lead-facts';

export type FactDecisionPayload = {
    /** The stored suggestion row's id. Every displayed chip is a stored row now
     * (both LLM and regex suggestions are persisted as pending rows first). */
    id: number;
    /** 'accepted' writes through to the lead's column; 'rejected' is remembered so
     * the same value is not re-suggested. */
    status: 'accepted' | 'rejected';
    /** Optional edited value to apply before deciding (edit-then-accept). */
    value?: string;
};

/* Accepting a decision writes through to the lead's own columns, so the lead
   caches are invalidated alongside the suggestions on every mutation. */
const useInvalidateOnFactChange = (leadUuid: string) => {
    const queryClient = useQueryClient();
    return () => {
        queryClient.invalidateQueries(getLeadFactsQueryKey(leadUuid));
        queryClient.invalidateQueries(queryKeys.leads);
    };
};

/**
 * Accept or reject a stored suggestion by id — the single decision path for both LLM
 * and regex suggestions (regex values are persisted as pending rows first, see
 * usePersistRegexSuggestions). Manual fact edits don't come here — they go through the
 * lead-edit endpoint, straight to the lead's columns.
 */
export const useDecideFact = (leadUuid: string) => {
    const invalidate = useInvalidateOnFactChange(leadUuid);
    const { addNotification } = useNotifications();

    return useMutation<ILeadFactSuggestion, IStandardErrorResponse, FactDecisionPayload>(
        async ({ id, status, value }) => {
            const row = await apiClient(`${GET_LEADS_PATH}/${leadUuid}/suggestions/${id}`, {
                method: 'patch',
                json: value !== undefined ? { status, suggested_value: value } : { status },
            }).json<LeadFactSuggestionWire>();
            return fromWire(row);
        },
        {
            onSuccess: invalidate,
            onError: (error) => {
                addNotification(getApiErrorMessage(error) || 'Failed to save lead fact', 'error');
            },
        }
    );
};
